import { i18n } from '@i18n/i18n.service';
import type { Flight, Leg } from '@models/landing/api/api-response.model';
import type { Airport, FareBrand } from '@models/master-data/master-data.model';
import { toDurationParts } from '@utils/date-time.util';
import {
  getTerminals,
  type TerminalItem,
  type ExtractedFlightLegData,
  getAirportsItems,
  getFareBrands,
} from '@helper/flight-details.mapper';

const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1_000;
export const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const MILLISECONDS_PER_DAY =
  HOURS_PER_DAY * MINUTES_PER_HOUR * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;

export const MILLISECONDS_PER_MINUTE = SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;

export interface TranslationOptions {
  defaultValue?: string;
  [key: string]: unknown;
}

export type Translate = (key: string, options?: TranslationOptions) => string;

export const resolveTerminalDisplay = (terminal: string, code?: string): string => {
  const terminals = getTerminals();
  const normalizedTerminal = terminal.toLowerCase();
  const terminalInfo = terminals.filter(
    (item: TerminalItem) => item.terminal.toLowerCase() === normalizedTerminal,
  );
  if (terminalInfo.length === 0) return terminal;

  const findCntxTerminalInfo = terminalInfo.find(
    (item: TerminalItem) => (code ? item.context?.includes(code) : false) ?? false,
  );

  if (findCntxTerminalInfo) {
    return findCntxTerminalInfo.display;
  } else {
    const match = terminalInfo[0];
    return match?.display ?? terminal;
  }
};

export const createTranslator = (translate?: Translate): Translate => {
  if (translate) {
    return translate;
  }

  return (key: string, options?: TranslationOptions): string => {
    return i18n.t(key, options ?? { defaultValue: key }).toString();
  };
};

export const formatMinutesDuration = (
  totalMinutes: number,
  t: Translate,
  minKey = 'Mins.Text',
): string => {
  const { hours, minutes } = toDurationParts(totalMinutes);
  return `${hours}${t('Hrs.Text')} ${minutes}${t(minKey)}`;
};

export const formatCheckInDuration = (
  remainingTimeToCheckin: string | null | undefined,
  t: Translate,
  minKey = 'Mins.Text',
): string => {
  const normalizedRemainingTime =
    typeof remainingTimeToCheckin === 'string'
      ? remainingTimeToCheckin
      : String(remainingTimeToCheckin ?? '');
  const parts = normalizedRemainingTime.split(':');
  const hours = Number(parts?.[0]);
  const minutes = Number(parts?.[1]);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return normalizedRemainingTime;
  }

  return formatMinutesDuration(hours * SECONDS_PER_MINUTE + minutes, t, minKey);
};

export const formatTerminal = (city: string, terminal: string | null, code: string): string => {
  if (!terminal) {
    return city;
  }

  const displayValue = resolveTerminalDisplay(terminal, code);

  return `${city}, ${displayValue}`;
};

export const getAirportCityByCode = (airportCode: string): { city: string; value: string } => {
  if (!airportCode) return { city: '', value: '' };
  const airport = getAirportsItems().find((item: Airport) => item.code === airportCode);
  return airport && typeof airport.city === 'string'
    ? { city: airport.city, value: airport.name }
    : { city: '', value: '' };
};

export const buildFlightNumber = (legs: Leg[]): string => {
  const uniqueNumbers = Array.from(
    new Set(legs.map((leg: Leg) => `${leg.sellingCarrier} ${leg.flightNum}`.trim())),
  );
  return uniqueNumbers.join(' / ');
};

export const shouldShowWait = (flight: Flight): boolean => {
  if (flight.stops === null || !Array.isArray(flight.stops)) {
    return false;
  }

  return flight.stops?.[0].notConnection ?? false;
};

export const getClassValue = (leg: Leg | ExtractedFlightLegData): string => {
  const fareType = leg.fare.webFareType;
  const fareBrands = getFareBrands();
  const matchedFareBrand = fareBrands.find(
    (fareBrand: FareBrand) => fareBrand.code.toLowerCase() === fareType.toLowerCase(),
  );
  return matchedFareBrand?.alternateDisplay ?? '';
};

// Parse date string in '15 February 2026, Sunday' format to Date object
function parseCustomDate(dateStr?: string): Date | null {
  if (!dateStr) return null;
  // Remove the day of week if present
  const parts = dateStr.split(',');
  const datePart = parts[0].trim();
  // Example: '15 February 2026'
  const parsed = Date.parse(datePart);
  if (!isNaN(parsed)) return new Date(parsed);
  return null;
}

// Returns the number of days between departure and arrival if arrival is after departure, otherwise 0
export function daysDifferenceCount(departureDate?: string, arrivalDate?: string): number {
  if (!departureDate || !arrivalDate) return 0;
  const dep = parseCustomDate(departureDate);
  const arr = parseCustomDate(arrivalDate);
  if (!dep || !arr) return 0;
  dep.setHours(0, 0, 0, 0);
  arr.setHours(0, 0, 0, 0);
  return Math.round((arr.getTime() - dep.getTime()) / MILLISECONDS_PER_DAY);
}
